import { serverTimestamp, type Timestamp } from "firebase/firestore";
import { action, computed, makeObservable } from "mobx";
import type { Api } from "../../core/apis";
import { BaseModel, type BaseModelData } from "../../core/BaseModel";
import type { Stores } from "../../core/stores";
import { trackEvent } from "../../core/trackEvent";
import { sanitizeForFirestore } from "../../modules/core/sanitizeForFirestore";
import { log } from "../../ui/log";
import type { EditorValue } from "../../ui/YooptaEditor";
import { BusinessFeedEventType } from "../feed/BusinessFeedItem";
import type { FeedItemBusinessModuleStatusData } from "../feed/events/FeedItemBusinessModuleStatus";
import type {
  BusinessPlanModel,
  BusinessStaticUserInfoWithUserUid,
} from "../plan/BusinessPlanModel";
import { BusinessModuleState, type BusinessModuleType } from "./types";

export type Snapshot = Record<
  string,
  | string
  | number
  | (string | number)[]
  | CompletedData[]
  | Timestamp
  | { lat: number; lng: number }[]
>;

export type BusinessModuleDataType = BusinessModuleModel<
  Snapshot,
  BusinessModuleModelData<Snapshot>
>;

export interface BusinessModuleData {
  uid: string;
  title: string | null;
  emoji: string | null;
  descriptionYooptaEditor: EditorValue | null;
}

export type CompletedData = {
  userUid: string;
  completedAt: number;
  skipped: boolean; // set to true if skipped
};

export type BusinessModuleModelData<SnapshotType = object> = BaseModelData &
  BusinessModuleData & {
    type: BusinessModuleType;
    state: BusinessModuleState;
    customerUid: string;
    businessPlanUid: string;
    feedItemUid: string;

    completedBy: CompletedData[];
    excludedUsers: string[];

    lastActivity: Timestamp;
    snapshot: SnapshotType;
  };

export default abstract class BusinessModuleModel<
  SnapshotType extends Snapshot,
  T extends BusinessModuleModelData<SnapshotType>,
> extends BaseModel<T> {
  constructor(stores: Stores, api: Api, data: T) {
    super(stores, api, data);
    Object.assign(this.data, {
      title: data.title ?? null,
      lastActivity: data.lastActivity ?? {
        nanoseconds: 0,
        seconds: Math.round(Date.now() / 1000),
      },
      snapshot: data.snapshot ?? {},
      customerUid: data.customerUid ?? "",
      businessPlanUid: data.businessPlanUid ?? "",
      feedItemUid: data.feedItemUid ?? "",
      descriptionYooptaEditor: data.descriptionYooptaEditor ?? null,
      completedBy: data.completedBy ?? [],
      excludedUsers: data.excludedUsers ?? [],
    });

    makeObservable(this);
  }

  abstract hasDoneFlow: boolean;

  abstract updateSnapshotWithoutSave(): void;

  @action async toggleExcluded(userUid: string) {
    const alreadyExcluded = this.data.excludedUsers.includes(userUid);
    if (alreadyExcluded) {
      this.data.excludedUsers = this.data.excludedUsers.filter(
        (uid) => uid !== userUid,
      );
    } else {
      this.data.excludedUsers.push(userUid);

      this.data.completedBy = this.data.completedBy.filter(
        (d) => d.userUid !== userUid,
      );
    }

    this.updateTimestampWithoutSave();
    this.updateSnapshotWithoutSave();
    this.save();
  }

  @action archive() {
    return this.businessPlan.deleteBusinessModule(this.uid);
  }

  @action async toggleCompletedByUserIds(
    userUids: string[],
    isCompleted: boolean,
    wasSkipped?: boolean,
  ) {
    if (isCompleted) {
      userUids.forEach((userUid) => {
        if (!this.data.completedBy.some((d) => d.userUid)) {
          this.data.completedBy.push({
            userUid,
            completedAt: Date.now(),
            skipped: wasSkipped ?? false,
          });
        }
      });
    } else {
      this.data.completedBy = this.data.completedBy.filter(
        (d) => !userUids.includes(d.userUid),
      );
    }

    this.updateTimestampWithoutSave();
    this.updateSnapshotWithoutSave();
    await this.save();

    if (isCompleted) {
      // Bump up the feed item on completions
      await this.updateFeedItemTimestampToNow();
    }
  }

  @action async toggleCompletedByUserId(userUid: string, wasSkipped?: boolean) {
    const isCompletedNow = !this.data.completedBy.some((d) => d.userUid);
    if (isCompletedNow) {
      this.data.completedBy.push({
        userUid,
        completedAt: Date.now(),
        skipped: wasSkipped ?? false,
      });
    } else {
      this.data.completedBy = this.data.completedBy.filter(
        (d) => d.userUid !== userUid,
      );
    }

    this.updateTimestampWithoutSave();
    this.updateSnapshotWithoutSave();
    await this.save();

    if (isCompletedNow && !wasSkipped) {
      // Bump up the feed item on completions
      await this.updateFeedItemTimestampToNow();
    }
  }

  @computed get isCompletedData(): CompletedData | undefined {
    if (!this.userUid) {
      return undefined;
    }

    return this.data.completedBy.find((d) => d.userUid === this.userUid);
  }

  @computed get completedUserInfo(): (BusinessStaticUserInfoWithUserUid &
    CompletedData)[] {
    const users: (BusinessStaticUserInfoWithUserUid & CompletedData)[] = [];
    this.data.completedBy
      .slice()
      .sort((_a, b) => (b.skipped ? 1 : -1))
      .forEach((c) => {
        const info = this.businessPlan.data.userInfo[c.userUid];
        if (info) {
          users.push({ ...c, ...info, userUid: c.userUid });
        }
      });
    return users;
  }

  @computed get isCompletedByUserOrTeam() {
    if (!this.userUid) {
      return false;
    }

    if (!this.businessPlan.planira) {
      return this.data.completedBy.some((d) => d.userUid === this.userUid);
    }

    return this.businessPlan.planira.data.userIds.every(
      (userUid) =>
        this.data.completedBy.some((d) => d.userUid === userUid) ||
        this.data.excludedUsers.includes(userUid),
    );
  }

  @computed get feedInfo(): {
    lastCompleted: CompletedData | undefined;
    totalCompleted: number;
    totalSkipped: number;
  } {
    return {
      lastCompleted: this.data.completedBy[this.data.completedBy.length - 1],
      totalCompleted: this.data.completedBy.length,
      totalSkipped: this.data.completedBy.filter((c) => c.skipped).length,
    };
  }

  @computed get isCompletedByUser() {
    if (!this.userUid) {
      return false;
    }

    return this.data.completedBy.some((d) => d.userUid === this.userUid);
  }

  @action async save() {
    return this.api.businessModulesApi.set(this);
  }

  @action async updateFeedItemTimestampToNow() {
    if (this.data.feedItemUid) {
      if (!this.feedItem) {
        await this.api.businessFeedItemsApi.getById(this.data.feedItemUid);
      }

      this.feedItem?.updateTimestampToNow();
    } else {
      const feedItem = await this.businessPlan.addToFeed(
        {
          type: BusinessFeedEventType.BusinessModuleStatus,
          moduleUid: "",
          itemData: {
            businessModuleUid: this.uid,
          } satisfies FeedItemBusinessModuleStatusData,
        },
        true,
      );

      this.data.feedItemUid = feedItem.uid;
      await this.api.businessModulesApi.update(this.uid, {
        feedItemUid: this.data.feedItemUid,
      });
    }
  }

  @computed get feedItem() {
    if (this.data.feedItemUid) {
      return this.stores.businessFeedItemsStore.byId.get(this.data.feedItemUid);
    }

    return null;
  }

  @action async setAsArchived(status = true) {
    this.data.archived = status;
    return this.api.businessModulesApi.update(this.uid, {
      archived: this.data.archived,
    });
  }

  @action async setFeedItemUid(uid = "") {
    this.data.feedItemUid = uid;
    return this.api.businessModulesApi.update(this.uid, {
      feedItemUid: this.data.feedItemUid,
    });
  }

  sendTelemetryEvent(event: string) {
    void trackEvent(`${this.data.type}_business_${event}`);
  }

  @action async updateSettings(newSettings: Partial<T>) {
    Object.assign(this.data, { ...this.data, ...newSettings });
    await this.api.businessModulesApi.set(this);
  }

  @computed get isEventTask(): boolean {
    return !!this.businessPlan.data.eventInfo.tasks.find(
      (i) => i.businessModuleUid === this.uid,
    );
  }

  @computed get hasDescription() {
    return !!(
      this.data.descriptionYooptaEditor &&
      !!Object.keys(this.data.descriptionYooptaEditor).length
    );
  }

  @action setTitle = async (title: string, skipSave?: boolean) => {
    const oldTitle = this.data.title;

    if (oldTitle !== title) {
      this.data.title = title;
      if (!skipSave) {
        await this.api.businessModulesApi.update(this.uid, {
          title: this.data.title,
        });

        this.sendTelemetryEvent("title_updated");
      }
    }
  };

  @action setDescriptionEditor = async (data: EditorValue | null) => {
    const sanitizedData = {
      data: sanitizeForFirestore(data?.data),
      markdown: data?.markdown,
    };

    if (sanitizedData !== this.data.descriptionYooptaEditor) {
      this.data.descriptionYooptaEditor = sanitizedData;
      this.updateTimestampWithoutSave();
      await this.save();

      this.sendTelemetryEvent("text_updated");
    }
  };

  @action setEmoji = async (emoji: string, skipSave?: boolean) => {
    const oldEmoji = this.data.emoji;

    if (oldEmoji !== emoji) {
      this.data.emoji = emoji;
      if (!skipSave) {
        await this.api.businessModulesApi.update(this.uid, {
          emoji: this.data.emoji,
        });

        this.sendTelemetryEvent("emoji_updated");
      }
    }
  };

  @action async deleteModule() {
    this.api.businessModulesApi.delete(this);
    this.sendTelemetryEvent("deleted");
  }

  @action updateTimestampWithoutSave() {
    this.data.lastActivity = serverTimestamp() as Timestamp;

    // also update current user visit
    log.dev("Need to update user timestamp?");
    //   this.maybePlan?.userInPlan?.updateVisitedTimestamp(this.uid);
  }

  @action updateTimestampWithSave() {
    this.updateTimestampWithoutSave();
    this.api.businessModulesApi.update(this.uid, {
      lastActivity: this.data.lastActivity,
    });
  }

  @computed get userUid() {
    return this.businessPlan.userInPlan?.uid;
  }

  @computed get businessPlan(): BusinessPlanModel {
    const businessPlan = this.stores.businessPlansStore.byId.get(
      this.data.businessPlanUid,
    );

    if (!businessPlan) {
      throw Error("No businessplan found in module.");
    }

    return businessPlan;
  }

  @computed get isDone() {
    return this.data.state === BusinessModuleState.Done;
  }

  toFireStore(): Omit<BusinessModuleModelData<SnapshotType>, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }

  abstract render(): JSX.Element;

  abstract renderBlock(props: {
    active?: boolean;
    upcoming?: boolean;
  }): JSX.Element;

  abstract renderFeedItem(): JSX.Element;

  abstract renderIcon(size: number): JSX.Element;
}
